import { Box, Stack, Typography } from '@mui/material';
import type { BetslipWithBetsExtendedInfo } from '../../../../@types';
import useLocalization from '../../../../hooks/useLocalization';
import { useSkinnedReofferedColor } from '../../../../hooks/useSkinnedReofferedColor';
import { FormattedBalance } from '../../../atoms/FormattedBalance';
import OddsDisplay from '../../../atoms/OddsDisplay';

import { mergeSx } from '../../../../utils/styles';

const styles = {
  container: {
    gap: 0.5,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    typography: 'body3',
    fontWeight: 600,
    color: 'neutral.300',
  },
  value: {
    typography: 'body3',
    fontWeight: 700,
    color: 'neutral.300',
  },
  previousValue: {
    typography: 'body3',
    fontWeight: 700,
    color: 'neutral.300',
    textDecoration: 'line-through',
    opacity: 0.5,
    mr: 0.5,
  },
} as const;

type NeobetReofferedSummaryProps = {
  betslip: BetslipWithBetsExtendedInfo;
  reofferedFromBetslip?: BetslipWithBetsExtendedInfo;
  showBonusData: boolean;
};

const NeobetReofferedSummary = ({ betslip, reofferedFromBetslip, showBonusData }: NeobetReofferedSummaryProps) => {
  const { t } = useLocalization();
  const getReofferedColor = useSkinnedReofferedColor();

  const { color: totalOddsColor, changed: totalOddsChanged } = getReofferedColor(
    betslip.totalOdds,
    reofferedFromBetslip?.totalOdds
  );
  const { color: stakeAmountColor, changed: stakeAmountChanged } = getReofferedColor(
    betslip.stakeAmountPerCombination,
    reofferedFromBetslip?.stakeAmountPerCombination
  );
  const { color: maxPayoutColor, changed: maxPayoutChanged } = getReofferedColor(
    betslip.maxPayout,
    reofferedFromBetslip?.maxPayout
  );

  return (
    <Stack sx={styles.container}>
      <Box sx={styles.row}>
        <Typography sx={styles.label}>{t('totalOdds', 'Total Odds')}</Typography>
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
          }}
        >
          {totalOddsChanged && reofferedFromBetslip?.totalOdds != null && (
            <OddsDisplay value={reofferedFromBetslip.totalOdds} sx={styles.previousValue} />
          )}
          <OddsDisplay value={betslip.totalOdds ?? 0} sx={{ ...styles.value, color: totalOddsColor }} />
        </Stack>
      </Box>

      <Box sx={styles.row}>
        <Typography sx={styles.label}>{t('stake', 'Stake')}</Typography>
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
          }}
        >
          {stakeAmountChanged && reofferedFromBetslip && (
            <FormattedBalance
              balance={reofferedFromBetslip.stakeAmountPerCombination}
              variant='body3'
              sx={mergeSx(
                {
                  fontWeight: 700,
                },
                styles.previousValue
              )}
            />
          )}
          <FormattedBalance
            balance={betslip.stakeAmountPerCombination}
            variant='body3'
            sx={{
              fontWeight: 700,
              color: stakeAmountColor,
            }}
          />
        </Stack>
      </Box>

      {showBonusData && (
        <Box sx={styles.row}>
          <Typography sx={styles.label}>
            {t('bonus', 'Bonus')} ({betslip.bonus?.bonusPercentage}%)
          </Typography>
          <FormattedBalance
            balance={betslip.bonus?.bonusAmount ?? 0}
            variant='body3'
            sx={{
              fontWeight: 700,
              color: 'neutral.300',
            }}
          />
        </Box>
      )}

      <Box sx={styles.row}>
        <Typography sx={styles.label}>{t('maxPossibleWinning', 'Max Possible Winning')}</Typography>
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
          }}
        >
          {maxPayoutChanged && reofferedFromBetslip && (
            <FormattedBalance
              balance={reofferedFromBetslip.maxPayout}
              variant='body3'
              sx={mergeSx(
                {
                  fontWeight: 700,
                },
                styles.previousValue
              )}
            />
          )}
          <FormattedBalance
            balance={betslip.maxPayout}
            variant='body3'
            sx={{
              fontWeight: 800,
              color: maxPayoutColor,
            }}
          />
        </Stack>
      </Box>
    </Stack>
  );
};

export default NeobetReofferedSummary;
